import React, { useEffect, useState, useMemo, useCallback } from "react";
import { fetchStoresItems, bulkMarkDelivered, bulkMarkCollected } from "../api/stores";
import api from "../api/api";
import Swal from "sweetalert2";
import "../styles/StoresPage.css";

const StoresPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedLots, setExpandedLots] = useState({});
  const [activeTab, setActiveTab] = useState("ordered");
  const [selectedItems, setSelectedItems] = useState({});
  const [partialInputs, setPartialInputs] = useState({});
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterRequester, setFilterRequester] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await fetchStoresItems();
      setRows(data);
    } catch (err) {
      Swal.fire("Error", "Failed to load store items", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(rows.map((r) => r.category_name).filter(Boolean))];
    return cats.sort();
  }, [rows]);

  const requesters = useMemo(() => {
    const reqs = [...new Set(rows.map((r) => r.requester_name).filter(Boolean))];
    return reqs.sort();
  }, [rows]);

  const locations = useMemo(() => {
    const locs = [...new Set(rows.map((r) => r.location_name).filter(Boolean))];
    return locs.sort();
  }, [rows]);

  const groupByLot = useCallback((list) => {
    return list.reduce((acc, item) => {
      const lot = item.lot_display || "Unassigned";
      if (!acc[lot]) acc[lot] = [];
      acc[lot].push(item);
      return acc;
    }, {});
  }, []);

  const toggleLotExpand = useCallback((lot) => {
    setExpandedLots((prev) => ({ ...prev, [lot]: !prev[lot] }));
  }, []);

  const toggleSelectItem = useCallback((lot, id) => {
    setSelectedItems((prev) => {
      const lotSelections = prev[lot] || [];
      return {
        ...prev,
        [lot]: lotSelections.includes(id)
          ? lotSelections.filter((itemId) => itemId !== id)
          : [...lotSelections, id],
      };
    });
  }, []);

  const handlePartialToggle = useCallback((lot, id) => {
    setPartialInputs((prev) => ({
      ...prev,
      [lot]: {
        ...prev[lot],
        [id]: prev[lot]?.[id] ? null : { qty: "", remarks: "" },
      },
    }));
  }, []);

  const handlePartialChange = useCallback((lot, id, field, value) => {
    setPartialInputs((prev) => ({
      ...prev,
      [lot]: { ...prev[lot], [id]: { ...prev[lot][id], [field]: value } },
    }));
  }, []);

  const validatePartialQuantities = useCallback((lot, payload) => {
    for (const p of payload) {
      const item = rows.find((r) => r.id === p.id);
      if (!item) continue;
      if (p.quantity < 1 || p.quantity > item.quantity) {
        Swal.fire(
          "Invalid Quantity",
          `Partial quantity for "${item.item}" must be between 1 and ${item.quantity}.`,
          "warning"
        );
        return false;
      }
    }
    return true;
  }, [rows]);

  const promptNotAvailableReason = useCallback(async (count = 1) => {
    return Swal.fire({
      title: "🚫 Mark as Not Available in Market",
      html: `
        <p style="margin-bottom:12px;color:#6b7280;font-size:14px;">
          ${count > 1 ? `${count} items` : "This item"} will be flagged as not available.
          The requester(s) and finance team will be notified.
        </p>
        <textarea id="swal-reason" class="swal2-textarea" placeholder="Reason (optional)" rows="3"
          style="resize:none;font-size:14px;"></textarea>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Mark Not Available",
      confirmButtonColor: "#e53e3e",
      cancelButtonText: "Cancel",
      cancelButtonColor: "#9ca3af",
      preConfirm: () => document.getElementById("swal-reason")?.value?.trim() || "",
    });
  }, []);

  const handleSubmitLot = useCallback(async (lot) => {
    const itemIds = selectedItems[lot] || [];
    if (!itemIds.length) {
      Swal.fire("⚠️ No Selection", "Please select items first.", "warning");
      return;
    }

    const payload = itemIds.map((id) => {
      const item = rows.find((r) => r.id === id);
      const partial = partialInputs[lot]?.[id];
      return partial
        ? { id, quantity: Number(partial.qty), remarks: partial.remarks || "", store_status: "partially_delivered" }
        : { id, quantity: item.quantity, remarks: item.remarks || "", store_status: "delivered" };
    });

    if (!validatePartialQuantities(lot, payload)) return;

    const confirm = await Swal.fire({
      title: `Submit ${itemIds.length} item(s) in lot ${lot} for delivery?`,
      text: "This action will mark selected items as delivered.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Deliver",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      setProcessing(true);
      await bulkMarkDelivered(payload);
      Swal.fire("🚚 Delivered!", `${itemIds.length} item(s) successfully submitted for delivery.`, "success");
      setSelectedItems((prev) => ({ ...prev, [lot]: [] }));
      setPartialInputs((prev) => ({ ...prev, [lot]: {} }));
      await fetchData();
    } catch (err) {
      Swal.fire("Error", "Failed to submit delivery.", "error");
    } finally {
      setProcessing(false);
    }
  }, [selectedItems, partialInputs, rows, validatePartialQuantities]);

  const handleSubmitCollection = useCallback(async (lot) => {
    const itemIds = selectedItems[lot] || [];
    if (!itemIds.length) {
      Swal.fire("⚠️ No Selection", "Please select items first.", "warning");
      return;
    }

    const payload = itemIds.map((id) => {
      const item = rows.find((r) => r.id === id);
      const partial = partialInputs[lot]?.[id];
      return partial
        ? { id, quantity: Number(partial.qty), remarks: partial.remarks || "", store_status: "partially_collected" }
        : { id, quantity: item.delivered_qty || item.quantity, remarks: item.remarks || "", store_status: "collected" };
    });

    if (!validatePartialQuantities(lot, payload)) return;

    const confirm = await Swal.fire({
      title: `Mark ${itemIds.length} item(s) in lot ${lot} as collected?`,
      text: "This action will mark selected items as collected from the store.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Yes, Collect",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      setProcessing(true);
      await bulkMarkCollected(payload);
      Swal.fire("📥 Collected!", `${itemIds.length} item(s) successfully marked as collected.`, "success");
      setSelectedItems((prev) => ({ ...prev, [lot]: [] }));
      setPartialInputs((prev) => ({ ...prev, [lot]: {} }));
      await fetchData();
    } catch (err) {
      Swal.fire("Error", "Failed to mark collected.", "error");
    } finally {
      setProcessing(false);
    }
  }, [selectedItems, partialInputs, rows, validatePartialQuantities]);

  const handleMarkAllFull = useCallback(async (lot, type) => {
    const items = groupByLot(rows)[lot] || [];
    const payload = items.map((item) => ({
      id: item.id,
      quantity: type === "delivery" ? item.quantity : item.delivered_qty || item.quantity,
      remarks: item.remarks || "",
      store_status: type === "delivery" ? "delivered" : "collected",
    }));

    const confirm = await Swal.fire({
      title: `Mark all ${items.length} items in lot ${lot} as fully ${type}?`,
      text: `This will mark every item in the lot as ${type === "delivery" ? "delivered" : "collected"}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, Mark All ${type === "delivery" ? "Delivered" : "Collected"}`,
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      setProcessing(true);
      if (type === "delivery") await bulkMarkDelivered(payload);
      else await bulkMarkCollected(payload);

      Swal.fire(
        `✅ All ${type === "delivery" ? "Delivered" : "Collected"}!`,
        `All ${items.length} items in lot ${lot} are now marked as ${type === "delivery" ? "delivered" : "collected"}.`,
        "success"
      );
      setSelectedItems((prev) => ({ ...prev, [lot]: [] }));
      setPartialInputs((prev) => ({ ...prev, [lot]: {} }));
      await fetchData();
    } catch (err) {
      Swal.fire("Error", `Failed to mark items as ${type === "delivery" ? "delivered" : "collected"}.`, "error");
    } finally {
      setProcessing(false);
    }
  }, [rows, groupByLot]);

  const handleBulkNotAvailable = useCallback(async (lot) => {
    const itemIds = selectedItems[lot] || [];
    if (!itemIds.length) {
      Swal.fire("⚠️ No Selection", "Please select items first.", "warning");
      return;
    }

    const result = await promptNotAvailableReason(itemIds.length);
    if (!result.isConfirmed) return;

    const payload = itemIds.map((id) => ({ id }));

    try {
      setProcessing(true);
      await api.post("/stores/mark-not-available-bulk/", {
        items: payload,
        reason: result.value || "",
      });
      Swal.fire("🚫 Flagged!", `${itemIds.length} item(s) marked as not available in market.`, "success");
      setSelectedItems((prev) => ({ ...prev, [lot]: [] }));
      await fetchData();
    } catch (err) {
      Swal.fire("Error", "Failed to mark items as not available.", "error");
    } finally {
      setProcessing(false);
    }
  }, [selectedItems, promptNotAvailableReason]);

  const handleMarkAllNotAvailable = useCallback(async (lot) => {
    const items = groupByLot(rows)[lot] || [];
    const result = await promptNotAvailableReason(items.length);
    if (!result.isConfirmed) return;

    const payload = items.map((item) => ({ id: item.id }));

    try {
      setProcessing(true);
      await api.post("/stores/mark-not-available-bulk/", {
        items: payload,
        reason: result.value || "",
      });
      Swal.fire("🚫 Flagged!", `All ${items.length} items in lot ${lot} marked as not available in market.`, "success");
      setSelectedItems((prev) => ({ ...prev, [lot]: [] }));
      await fetchData();
    } catch (err) {
      Swal.fire("Error", "Failed to mark items as not available.", "error");
    } finally {
      setProcessing(false);
    }
  }, [rows, groupByLot, promptNotAvailableReason]);

  const handleSingleNotAvailable = useCallback(async (item) => {
    const result = await promptNotAvailableReason(1);
    if (!result.isConfirmed) return;

    try {
      setProcessing(true);
      await api.post(`/stores/${item.id}/mark-not-available/`, { reason: result.value || "" });
      Swal.fire("🚫 Flagged!", `"${item.item}" marked as not available in market.`, "success");
      await fetchData();
    } catch (err) {
      Swal.fire("Error", "Failed to mark item as not available.", "error");
    } finally {
      setProcessing(false);
    }
  }, [promptNotAvailableReason]);

  const handleSort = useCallback((field) => {
    setSortDirection((prev) => (sortField === field ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortField(field);
  }, [sortField]);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
  }, []);

  const formatDateTime = useCallback((dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const matchesSearch =
        !search ||
        r.item?.toLowerCase().includes(search.toLowerCase()) ||
        r.description?.toLowerCase().includes(search.toLowerCase()) ||
        r.requester_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.lot_display?.toLowerCase().includes(search.toLowerCase()) ||
        r.cost_centre?.toLowerCase().includes(search.toLowerCase());

      const matchesCategory = !filterCategory || r.category_name === filterCategory;
      const matchesRequester = !filterRequester || r.requester_name === filterRequester;
      const matchesLocation = !filterLocation || r.location_name === filterLocation;

      let matchesDate = true;
      if (filterDateFrom || filterDateTo) {
        const created = r.created_at ? new Date(r.created_at) : null;
        if (filterDateFrom && created) matchesDate = matchesDate && created >= new Date(filterDateFrom);
        if (filterDateTo && created) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59);
          matchesDate = matchesDate && created <= toDate;
        }
      }

      return matchesSearch && matchesCategory && matchesRequester && matchesLocation && matchesDate;
    });
  }, [rows, search, filterCategory, filterRequester, filterLocation, filterDateFrom, filterDateTo]);

  // Only ordered and delivered tabs — NOT_AVAILABLE is hidden from the frontend
  const tabRows = useMemo(() => {
    return filteredRows.filter((r) => {
      if (activeTab === "ordered") return r.status === "ORDERED";
      if (activeTab === "delivered") return r.status === "DELIVERED";
      return false;
    });
  }, [filteredRows, activeTab]);

  const sortedTabRows = useMemo(() => {
    const sorted = [...tabRows];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";

      if (sortField.includes("_at") || sortField === "intended_delivery_date") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        // keep as-is
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [tabRows, sortField, sortDirection]);

  const grouped = useMemo(() => groupByLot(sortedTabRows), [sortedTabRows, groupByLot]);
  const lotKeys = useMemo(() => Object.keys(grouped), [grouped]);
  const indexOfLastLot = currentPage * itemsPerPage;
  const indexOfFirstLot = indexOfLastLot - itemsPerPage;
  const currentLots = useMemo(() => lotKeys.slice(indexOfFirstLot, indexOfLastLot), [lotKeys, indexOfFirstLot, indexOfLastLot]);
  const totalPages = useMemo(() => Math.ceil(lotKeys.length / itemsPerPage), [lotKeys, itemsPerPage]);

  const stats = useMemo(() => ({
    total: filteredRows.length,
    ordered: filteredRows.filter((r) => r.status === "ORDERED").length,
    delivered: filteredRows.filter((r) => r.status === "DELIVERED").length,
  }), [filteredRows]);

  const tabCounts = useMemo(() => ({
    ordered: filteredRows.filter((r) => r.status === "ORDERED").length,
    delivered: filteredRows.filter((r) => r.status === "DELIVERED").length,
  }), [filteredRows]);

  const clearFilters = useCallback(() => {
    setSearch("");
    setFilterCategory("");
    setFilterRequester("");
    setFilterLocation("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSortField("created_at");
    setSortDirection("desc");
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    setSelectedItems({});
    setPartialInputs({});
    setCurrentPage(1);
  }, [activeTab]);

  return (
    <div className="stores-page">
      {/* ── Page Header ── */}
      <header className="page-header">
        <div className="page-header-inner">
          <div>
            <h2 className="page-title">
              <span className="page-title-icon">📦</span>
              <div>
                <div>Stores Dashboard</div>
                <div className="page-subtitle">Manage inventory, deliveries & collections</div>
              </div>
            </h2>
          </div>
        </div>
      </header>

      {/* ── Stats Bar — NOT_AVAILABLE stat card removed ── */}
      <div className="stats-bar">
        <div className="stat-card">
          <div className="stat-icon total">📊</div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Items</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon ordered">🚚</div>
          <div className="stat-content">
            <div className="stat-value">{stats.ordered}</div>
            <div className="stat-label">Ordered / Pending</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon delivered">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.delivered}</div>
            <div className="stat-label">Delivered / Waiting</div>
          </div>
        </div>
      </div>

      {/* ── Controls Section ── */}
      <div className="controls-section">
        <div className="controls-card">
          <div className="controls-toolbar">
            <div className="search-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="search-bar"
                placeholder="Search by Item, Description, Requester, Lot, Cost Centre..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              />
            </div>

            {/* ── Tabs — only Ordered and Delivered ── */}
            <div className="tabs-container">
              <button
                className={`tab-btn ${activeTab === "ordered" ? "active" : ""}`}
                onClick={() => setActiveTab("ordered")}
              >
                🚚 Ordered
                {tabCounts.ordered > 0 && <span className="tab-badge">{tabCounts.ordered}</span>}
              </button>
              <button
                className={`tab-btn ${activeTab === "delivered" ? "active" : ""}`}
                onClick={() => setActiveTab("delivered")}
              >
                ✅ Delivered
                {tabCounts.delivered > 0 && <span className="tab-badge">{tabCounts.delivered}</span>}
              </button>
            </div>

            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowFilters((p) => !p)}
            >
              {showFilters ? "🔼 Hide Filters" : "🔽 Show Filters"}
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <>
              <div className="filter-bar">
                <div className="filter-group">
                  <span className="filter-label">Category:</span>
                  <select
                    className="filter-select"
                    value={filterCategory}
                    onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <span className="filter-label">Requester:</span>
                  <select
                    className="filter-select"
                    value={filterRequester}
                    onChange={(e) => { setFilterRequester(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="">All Requesters</option>
                    {requesters.map((req) => (
                      <option key={req} value={req}>{req}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <span className="filter-label">Location:</span>
                  <select
                    className="filter-select"
                    value={filterLocation}
                    onChange={(e) => { setFilterLocation(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="">All Locations</option>
                    {locations.map((loc) => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <span className="filter-label">Created From:</span>
                  <input
                    type="date"
                    className="filter-input"
                    value={filterDateFrom}
                    onChange={(e) => { setFilterDateFrom(e.target.value); setCurrentPage(1); }}
                  />
                </div>

                <div className="filter-group">
                  <span className="filter-label">To:</span>
                  <input
                    type="date"
                    className="filter-input"
                    value={filterDateTo}
                    onChange={(e) => { setFilterDateTo(e.target.value); setCurrentPage(1); }}
                  />
                </div>

                <button className="btn btn-outline btn-sm" onClick={clearFilters}>
                  🔄 Clear All
                </button>

                <div className="page-size">
                  <label>Show:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  >
                    {[10, 20, 30, 50, 100].map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                  <span>lots/page</span>
                </div>
              </div>

              {/* Sort Bar */}
              <div className="sort-bar">
                <span className="filter-label">Sort by:</span>
                {[
                  { key: "created_at", label: "Created At" },
                  { key: "ordered_at", label: "Ordered At" },
                  { key: "item", label: "Item Name" },
                  { key: "quantity", label: "Quantity" },
                  { key: "requester_name", label: "Requester" },
                  { key: "lot_display", label: "Lot" },
                  { key: "category_name", label: "Category" },
                  { key: "cost_centre", label: "Cost Centre" },
                  { key: "delivered_at", label: "Delivered At" },
                  { key: "collected_at", label: "Collected At" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    className={`sort-btn ${sortField === opt.key ? "active" : ""}`}
                    onClick={() => handleSort(opt.key)}
                  >
                    {opt.label}
                    {sortField === opt.key && (
                      <span className="sort-icon">{sortDirection === "asc" ? "▲" : "▼"}</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Lots Container ── */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading store items...</p>
        </div>
      ) : (
        <div className="lots-container">
          {currentLots.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-card">
                <div className="empty-state-icon">📭</div>
                <div className="empty-state-title">No Items Found</div>
                <div className="empty-state-text">
                  Try adjusting your search or filters to find what you're looking for.
                </div>
              </div>
            </div>
          ) : (
            currentLots.map((lot) => {
              const items = grouped[lot];
              const allSelected =
                selectedItems[lot]?.length === items.length && items.length > 0;

              return (
                <div key={lot} className="lot-card">
                  {/* Lot Header */}
                  <div className="lot-header">
                    <div className="lot-header-left">
                      <div className="lot-icon">📦</div>
                      <div className="lot-info">
                        <div className="lot-title">Lot {lot}</div>
                        <div className="lot-meta">{items.length} item(s) · {items[0]?.category_name || "—"}</div>
                      </div>
                    </div>

                    <div className="lot-header-right">
                      <label className="select-all-label">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() =>
                            setSelectedItems((prev) => ({
                              ...prev,
                              [lot]: allSelected ? [] : items.map((i) => i.id),
                            }))
                          }
                        />
                        Select All
                      </label>

                      <button className="expand-btn" onClick={() => toggleLotExpand(lot)}>
                        {expandedLots[lot] ? "▲ Collapse" : "▼ Expand"}
                      </button>

                      {/* ── ORDERED tab actions ── */}
                      {activeTab === "ordered" && (
                        <div className="lot-actions">
                          <button className="btn btn-primary btn-sm" onClick={() => handleSubmitLot(lot)}>
                            💾 Submit Delivery
                          </button>
                          <button className="btn btn-success btn-sm" onClick={() => handleMarkAllFull(lot, "delivery")}>
                            ✅ Mark All Delivered
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleBulkNotAvailable(lot)}>
                            🚫 Selected N/A
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleMarkAllNotAvailable(lot)}>
                            🚫 All N/A
                          </button>
                        </div>
                      )}

                      {/* ── DELIVERED tab actions ── */}
                      {activeTab === "delivered" && (
                        <div className="lot-actions">
                          <button className="btn btn-primary btn-sm" onClick={() => handleSubmitCollection(lot)}>
                            📥 Mark Collected
                          </button>
                          <button className="btn btn-success btn-sm" onClick={() => handleMarkAllFull(lot, "collection")}>
                            ✅ Mark All Collected
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleBulkNotAvailable(lot)}>
                            🚫 Selected N/A
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleMarkAllNotAvailable(lot)}>
                            🚫 All N/A
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Lot Table */}
                  {expandedLots[lot] && (
                    <div className="lot-table-wrapper">
                      <table className="lot-table">
                        <thead>
                          <tr>
                            <th>Select</th>
                            <th>Item</th>
                            <th>Qty</th>
                            <th>Delivered Qty</th>
                            <th>Stock Balance</th>
                            <th>Units</th>
                            <th>Cost Centre</th>
                            <th>Requester</th>
                            <th>Location</th>
                            <th>Status</th>
                            <th>Created At</th>
                            <th>Ordered At</th>
                            {activeTab === "delivered" && <th>Delivered At</th>}
                            <th>Partial</th>
                            <th>Input Qty</th>
                            <th>Remarks</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((r) => {
                            const isSelected = selectedItems[lot]?.includes(r.id);
                            const isPartial = !!partialInputs[lot]?.[r.id];

                            return (
                              <tr key={r.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="row-checkbox"
                                    checked={isSelected || false}
                                    onChange={() => toggleSelectItem(lot, r.id)}
                                  />
                                </td>

                                {/* Item */}
                                <td>
                                  <div className="item-cell">
                                    <span className="item-name">{r.item}</span>
                                    <span className="item-description" title={r.description}>
                                      {r.description || "—"}
                                    </span>
                                  </div>
                                </td>

                                {/* Qty */}
                                <td><span className="qty-cell">{r.quantity}</span></td>

                                {/* Delivered Qty */}
                                <td><span className="qty-cell">{r.delivered_qty || 0}</span></td>

                                {/* Stock Balance */}
                                <td><span className="qty-cell">{r.stock_balance ?? "—"}</span></td>

                                {/* Units */}
                                <td><span className="qty-unit">{r.units || "—"}</span></td>

                                {/* Cost Centre */}
                                <td>{r.cost_centre || "—"}</td>

                                {/* Requester */}
                                <td>{r.requester_name || "—"}</td>

                                {/* Location */}
                                <td>{r.location_name || "—"}</td>

                                {/* Status */}
                                <td>
                                  <span className={`status-badge ${r.status?.toLowerCase()}`}>
                                    {r.status}
                                  </span>
                                </td>

                                {/* Created At */}
                                <td>
                                  <div className="date-cell">
                                    <span className="date-value">{formatDate(r.created_at)}</span>
                                    <span className="date-time">{formatDateTime(r.created_at)}</span>
                                  </div>
                                </td>

                                {/* Ordered At */}
                                <td>
                                  <div className="date-cell">
                                    <span className="date-value">{formatDate(r.ordered_at)}</span>
                                    <span className="date-time">{formatDateTime(r.ordered_at)}</span>
                                  </div>
                                </td>

                                {/* Delivered At (delivered tab only) */}
                                {activeTab === "delivered" && (
                                  <td>
                                    <div className="date-cell">
                                      <span className="date-value">{formatDate(r.delivered_at)}</span>
                                      <span className="date-time">{formatDateTime(r.delivered_at)}</span>
                                    </div>
                                  </td>
                                )}

                                {/* Partial toggle */}
                                <td>
                                  <button
                                    className={`btn ${isPartial ? "btn-outline" : "btn-ghost"} btn-xs`}
                                    onClick={() => handlePartialToggle(lot, r.id)}
                                  >
                                    {isPartial ? "Cancel" : "Partial"}
                                  </button>
                                </td>

                                {/* Input Qty */}
                                <td>
                                  {isPartial && (
                                    <input
                                      type="number"
                                      className="partial-input"
                                      min="1"
                                      max={r.quantity}
                                      value={partialInputs[lot][r.id]?.qty || ""}
                                      onChange={(e) => handlePartialChange(lot, r.id, "qty", e.target.value)}
                                    />
                                  )}
                                </td>

                                {/* Remarks */}
                                <td>
                                  {isPartial && (
                                    <input
                                      type="text"
                                      className="partial-remarks"
                                      placeholder="Add remarks..."
                                      value={partialInputs[lot][r.id]?.remarks || ""}
                                      onChange={(e) => handlePartialChange(lot, r.id, "remarks", e.target.value)}
                                    />
                                  )}
                                </td>

                                {/* Action */}
                                <td>
                                  <div className="action-cell">
                                    <button
                                      className="single-na-btn"
                                      onClick={() => handleSingleNotAvailable(r)}
                                      title="Mark as Not Available in Market"
                                    >
                                      🚫
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <span className="pagination-info">
            Showing {indexOfFirstLot + 1}–{Math.min(indexOfLastLot, lotKeys.length)} of {lotKeys.length} lots
          </span>
          <div className="pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
              ◀ Prev
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={currentPage === i + 1 ? "active" : ""}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
              Next ▶
            </button>
          </div>
        </div>
      )}

      {/* ── Processing Overlay ── */}
      {processing && (
        <div className="processing-overlay">
          <div className="spinner" />
          <p>Processing store actions... Please wait 📦</p>
        </div>
      )}
    </div>
  );
};

export default StoresPage;